"use client";
import { replaceLocationHref } from "@gettactic/helpers/src/navigation/replaceLocationHref";
import { getOrganizationNameFromHostname } from "@gettactic/helpers/src/url/getOrganizationNameFromHostname";
import { getOrganizationUrlFromSlug } from "@gettactic/helpers/src/url/getOrganizationUrlFromSlug";
import { isHostnameOrganizationSubDomain } from "@gettactic/helpers/src/url/isHostnameOrganizationSubDomain";
import { isVercelDeployment } from "@gettactic/helpers/src/vercel/vercelPreview";
import { useAuthenticated } from "@gettactic/hooks";
import React from "react";

export interface UseOrganizationRedirectOptions {
  /**
   * Carry the current URL's query string *and* fragment over to the org
   * subdomain. The fragment matters for the legacy `#goToMap=…` deep links that
   * older notifications point at: dropping it loses the resource to open.
   */
  preserveQueryString?: boolean;
}

export const useOrganizationRedirect = (
  url?: string,
  options?: UseOrganizationRedirectOptions
) => {
  const isSubDomain = isHostnameOrganizationSubDomain();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const firstOrganizationSlug =
    authenticatedUser?.organization?.slug ||
    authenticatedUser?.organizations?.[0]?.slug ||
    "";

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      // On Vercel preview deployments there is no per-org subdomain, so the
      // subdomain-based redirects below would always bounce to login. Org
      // selection is driven by the slug override saved in localStorage instead.
      if (isVercelDeployment()) return;
      if (
        window.location.hostname.startsWith("www.") ||
        window.location.search.includes("logout=true")
      ) {
        replaceLocationHref(
          `${process.env.NEXT_PUBLIC_TACTIC_DOMAIN_AND_PORT}${url || ""}`
        );
      } else {
        const organizationName =
          getOrganizationNameFromHostname() || firstOrganizationSlug;
        if (!isSubDomain || !organizationName) {
          replaceLocationHref("/auth/login?homepage=true");
          return;
        }
        replaceLocationHref(
          getOrganizationUrlFromSlug(
            organizationName,
            options?.preserveQueryString
              ? `${url || ""}${window.location.search}${window.location.hash}`
              : url
          )
        );
      }
    }
  }, [isSubDomain, firstOrganizationSlug]);
};
